import { Dialog, DialogProps } from '@eppendorf/vnls-react-components';
import { FunctionComponentElement, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { NotificationDialogProgressIndicator } from '$features/notifications/add-or-edit-notification-rule-dialog/notification-dialog-progress-indicator';
import { NotificationRuleDialogContent } from '$features/notifications/add-or-edit-notification-rule-dialog/notification-rule-dialog-content';
import { SuccessScreen } from '$features/notifications/add-or-edit-notification-rule-dialog/screens/success-screen';
import { useNotificationRuleDialogForm } from '$features/notifications/add-or-edit-notification-rule-dialog/useNotificationRuleDialogForm';
import {
  AddOrEditNotiRuleModalScreens,
  NotificationRuleDialogContentHandle,
  NotificationRuleMode,
} from '$features/notifications/types';

export interface AddNotificationRuleDialogProps {
  isOpen?: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: DialogProps['trigger'];
}

export function AddNotificationRuleDialog({
  isOpen = false,
  onOpenChange,
  trigger,
}: AddNotificationRuleDialogProps): FunctionComponentElement<AddNotificationRuleDialogProps> {
  const { t } = useTranslation();
  const {
    handleFormSubmit,
    isLoading,
    requestError,
    isSuccess,
    addedNotificationRule,
    formMethods,
  } = useNotificationRuleDialogForm(undefined, NotificationRuleMode.CREATE);
  const isNotificationTypesEnabled = useFeatureIsEnabled(
    'ESEVNLS_4596_NOTIFICATION_TYPES',
  );
  const [activeScreen, setActiveScreen] =
    useState<AddOrEditNotiRuleModalScreens>('deviceTypeScreen');
  const dialogContentRef = useRef<NotificationRuleDialogContentHandle>(null);

  function handleOpenChange(open: boolean): void {
    if (onOpenChange) onOpenChange(open);
  }

  const handleStepClick = useCallback((screen: AddOrEditNotiRuleModalScreens) => {
    dialogContentRef.current?.goToScreen(screen);
  }, []);

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={(open) => handleOpenChange(open)}
      trigger={trigger}
      className={activeScreen === 'errorScreen' || isSuccess ? 'w-max-4xl' : 'w-max-7xl'}
      hideCloseButton={activeScreen === 'errorScreen' || isSuccess}
    >
      {!isSuccess && activeScreen !== 'errorScreen' && (
        <Dialog.Title>{t('notificationRules.new')}</Dialog.Title>
      )}
      {isNotificationTypesEnabled && !isSuccess && activeScreen !== 'errorScreen' && (
        <NotificationDialogProgressIndicator
          activeScreen={activeScreen}
          onStepClick={handleStepClick}
        />
      )}
      {!isSuccess && (
        <NotificationRuleDialogContent
          ref={dialogContentRef}
          handleFormSubmit={handleFormSubmit}
          isLoading={isLoading}
          requestError={requestError}
          formMethods={formMethods}
          onScreenChange={setActiveScreen}
        />
      )}
      {isSuccess && !requestError && (
        <SuccessScreen
          mode={NotificationRuleMode.CREATE}
          notiRule={formMethods.getValues()}
          notificationRuleId={addedNotificationRule?.id}
        />
      )}
    </Dialog>
  );
}
